import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Hover popup za bar/cell elemente — vizualno usklađen s DashboardTooltip
 * (rounded-lg, shadow-xl, slate-800). Renderira se kroz PORTAL (position: fixed)
 * iznad roditeljskog elementa, pa ga ne reže `overflow` predaka (Grid, kartica…).
 *
 * Parent (bar/row kolona) je hover trigger — BarTooltip se stavlja kao dijete u njega:
 * ```tsx
 * <div className="... group/bar relative">
 *   <BarTooltip position="top">
 *     <BarTooltipTitle>Travanj 2026</BarTooltipTitle>
 *     <BarTooltipRow label="Poslano" value={5} />
 *   </BarTooltip>
 * </div>
 * ```
 */
interface BarTooltipProps {
  position?: "top" | "top-far"; // razmak iznad elementa
  align?: "center" | "right"; // center = centrirano iznad, right = poravnato uz desni rub
  groupName?: "bar" | "row"; // zadržano radi kompatibilnosti (hover ide preko roditelja)
  children: React.ReactNode;
}

const BarTooltip: React.FC<BarTooltipProps> = ({ position = "top", align = "center", children }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const parent = anchorRef.current?.parentElement;
    if (!parent) return;
    const show = () => setRect(parent.getBoundingClientRect());
    const hide = () => setRect(null);
    parent.addEventListener("mouseenter", show);
    parent.addEventListener("mousemove", show);
    parent.addEventListener("mouseleave", hide);
    return () => {
      parent.removeEventListener("mouseenter", show);
      parent.removeEventListener("mousemove", show);
      parent.removeEventListener("mouseleave", hide);
    };
  }, []);

  const gap = position === "top-far" ? 16 : 8;
  const style: React.CSSProperties = rect
    ? {
        position: "fixed",
        top: rect.top - gap,
        left: align === "right" ? rect.right : rect.left + rect.width / 2,
        transform: align === "right" ? "translate(-100%, -100%)" : "translate(-50%, -100%)",
      }
    : {};

  return (
    <>
      <span ref={anchorRef} className="hidden" />
      {rect &&
        createPortal(
          <div
            style={style}
            className="z-[60] min-w-[180px] bg-slate-800 text-white text-sm rounded-lg shadow-xl p-3 pointer-events-none"
          >
            {children}
          </div>,
          document.body
        )}
    </>
  );
};

export default BarTooltip;

export const BarTooltipTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">{children}</div>
);

interface BarTooltipRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  emphasis?: "default" | "positive" | "negative" | "muted";
}

export const BarTooltipRow: React.FC<BarTooltipRowProps> = ({ label, value, emphasis = "default" }) => {
  const valueClass =
    emphasis === "positive"
      ? "font-bold text-emerald-400"
      : emphasis === "negative"
        ? "font-bold text-rose-400"
        : emphasis === "muted"
          ? "text-slate-400"
          : "font-medium";
  return (
    <div className="flex justify-between text-sm mb-1 gap-4">
      <span className="text-slate-300 whitespace-nowrap">{label}</span>
      <span className={`whitespace-nowrap ${valueClass}`}>{value}</span>
    </div>
  );
};

export const BarTooltipDivider: React.FC = () => (
  <div className="border-t border-slate-600 my-2"></div>
);

/**
 * Manji popup namjenjen legendama (legenda items koriste plain `group` umjesto
 * `group/bar`). Vizualno isti stil kao BarTooltip — slate-800, rounded-lg, shadow-xl.
 * Default pozicioniran ISPOD legende (top-full) jer legenda obično sjedi na dnu
 * card-a — gore kolidira s tooltipovima iznad bar grafa.
 */
export const LegendTooltip: React.FC<{
  position?: "top" | "bottom";
  align?: "left" | "center" | "right";
  children: React.ReactNode;
}> = ({ position = "bottom", align = "center", children }) => {
  const positionClass = position === "top" ? "bottom-full mb-2" : "top-full mt-2";
  // Pune klase u stringovima — Tailwind JIT mora vidjeti sve varijante.
  const alignClass =
    align === "left" ? "left-0" : align === "right" ? "right-0" : "left-1/2 -translate-x-1/2";
  return (
    <div className={`absolute ${positionClass} ${alignClass} z-20 min-w-[180px] bg-slate-800 text-white text-sm rounded-lg shadow-xl p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`}>
      {children}
    </div>
  );
};
